import { useEffect, useMemo, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import api from "@/lib/api";
import ProgramCard from "@/components/ProgramCard";
import { Search, ArrowRight } from "lucide-react";

const CATEGORIES = [
  { id: "", label: "All" },
  { id: "business", label: "Business" },
  { id: "technology", label: "Technology" },
  { id: "leadership", label: "Leadership" },
  { id: "entrepreneurship", label: "Entrepreneurship" },
  { id: "culture", label: "Culture" },
  { id: "career", label: "Career" },
];

const DESTINATIONS_OPT = [
  { id: "", label: "All destinations" },
  { id: "canada", label: "Canada" },
  { id: "malaysia", label: "Malaysia" },
  { id: "indonesia", label: "Indonesia" },
  { id: "india", label: "India" },
];

const DURATIONS_OPT = [
  { id: "", label: "Any duration" },
  { id: "1 Week", label: "1 Week" },
  { id: "2 Weeks", label: "2 Weeks" },
  { id: "3 Weeks", label: "3 Weeks" },
];

export default function Programs() {
  const [sp, setSp] = useSearchParams();
  const [category, setCategory] = useState(sp.get("category") || "");
  const [destination, setDestination] = useState(sp.get("destination") || "");
  const [duration, setDuration] = useState(sp.get("duration") || "");
  const [q, setQ] = useState(sp.get("q") || "");
  const [programs, setPrograms] = useState([]);
  const [loading, setLoading] = useState(true);

  const query = useMemo(() => {
    const params = new URLSearchParams();
    if (category) params.set("category", category);
    if (destination) params.set("destination", destination);
    if (duration) params.set("duration", duration);
    if (q) params.set("q", q);
    return params.toString();
  }, [category, destination, duration, q]);

  useEffect(() => {
    setLoading(true);
    api.get(`/programs${query ? `?${query}` : ""}`)
      .then(({ data }) => setPrograms(data))
      .finally(() => setLoading(false));
    setSp(new URLSearchParams(query));
  }, [query, setSp]);

  let content;
  if (loading) {
    content = <p className="text-slate-500">Loading…</p>;
  } else if (programs.length === 0) {
    content = <p className="text-slate-500">No programs match those filters.</p>;
  } else {
    content = (
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="programs-grid">
        {programs.map((p, i) => <ProgramCard key={p.id} program={p} index={i} />)}
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-5 lg:px-8 py-16" data-testid="programs-page">
      <div className="max-w-3xl mb-10">
        <p className="text-sm font-bold text-blue-600 uppercase tracking-widest mb-3">Programs</p>
        <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold text-slate-900 leading-[1.05]">
          Explore Our Global <span className="text-purple-600">Learning Programs</span>
        </h1>
        <p className="mt-5 text-slate-600">
          Choose from 30+ programs across business, technology, leadership, entrepreneurship, culture, and career readiness. Every program can be delivered as a short-term travel experience.
        </p>
        <div className="mt-6">
          <Link
            to="/apply"
            data-testid="programs-cta-apply"
            className="inline-flex items-center gap-2 px-5 py-3 rounded-full bg-orange-500 hover:bg-orange-600 text-white font-semibold text-sm shadow-lg shadow-orange-500/25 hover:-translate-y-0.5 transition-transform"
          >
            Apply Now <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </div>

      {/* Filters */}
      <div className="rounded-3xl bg-white border border-slate-100 shadow-sm p-4 md:p-5 mb-10 grid gap-3 md:grid-cols-4" data-testid="programs-filters">
        <div className="relative md:col-span-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search programs…"
            data-testid="programs-search"
            className="w-full pl-9 pr-3 py-2.5 rounded-xl bg-slate-50 border border-transparent focus:border-blue-500 focus:bg-white text-sm outline-none transition-colors"
          />
        </div>
        <select
          value={category}
          onChange={(e) => setCategory(e.target.value)}
          data-testid="programs-filter-category"
          className="px-3 py-2.5 rounded-xl bg-slate-50 border border-transparent focus:border-blue-500 focus:bg-white text-sm outline-none transition-colors"
        >
          {CATEGORIES.map((c) => <option key={c.id} value={c.id}>{c.label}</option>)}
        </select>
        <select
          value={destination}
          onChange={(e) => setDestination(e.target.value)}
          data-testid="programs-filter-destination"
          className="px-3 py-2.5 rounded-xl bg-slate-50 border border-transparent focus:border-blue-500 focus:bg-white text-sm outline-none transition-colors"
        >
          {DESTINATIONS_OPT.map((c) => <option key={c.id} value={c.id}>{c.label}</option>)}
        </select>
        <select
          value={duration}
          onChange={(e) => setDuration(e.target.value)}
          data-testid="programs-filter-duration"
          className="px-3 py-2.5 rounded-xl bg-slate-50 border border-transparent focus:border-blue-500 focus:bg-white text-sm outline-none transition-colors"
        >
          {DURATIONS_OPT.map((c) => <option key={c.id} value={c.id}>{c.label}</option>)}
        </select>
      </div>

      {content}

      <p className="mt-12 text-sm text-slate-500 italic max-w-3xl">
        Many more customized programs are available for schools, colleges, universities, training centres, businesses, and student groups.
      </p>
    </div>
  );
}
